import { Body, Box, Flex, Image } from '@hover/blueprint';
import React from 'react';
import { generatePath, useHistory } from 'react-router';

import {
  useFormattedTimeAgo,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import type { PublicOrgInfo } from 'src/shared/graphql/generated';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';

import { SplitCard } from '../../Card';
import { formatSpaceType } from '../../Utils';

interface IProjectCard {
  imagePath?: string;
  title: string;
  spaceType?: string | null;
  createdAt: string;
  projectId: number;
  publicOrgInfo?: PublicOrgInfo | null;
  showOrgBranding: boolean;
}

const ProjectCard = ({
  imagePath,
  title,
  spaceType,
  createdAt,
  projectId,
  publicOrgInfo,
  showOrgBranding,
}: IProjectCard) => {
  const history = useHistory();
  const dateCreated = useFormattedTimeAgo(createdAt);
  const { trackingData } = useHomeownerAppContext();
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  const handleProjectClick = () => {
    buttonPressed({
      ...trackingData,
      project_name: title,
      button_text: 'Open Project',
      partner_id: partnerId,
      primary_cta: false,
      project_id: projectId,
      track_id: 'project-clicked',
    });
    history.push(
      generatePath(Routes.HOMEOWNER_APP_PROJECT.path, {
        projectId,
      }),
    );
  };

  const SubText = () => {
    if (showOrgBranding && publicOrgInfo) {
      return (
        <Body color="neutral.600" mt="100">
          {`Shared with ${publicOrgInfo.displayName}`}
        </Body>
      );
    }
    if (spaceType) {
      return (
        <Body color="neutral.600" mt="100">
          {formatSpaceType(spaceType)}
        </Body>
      );
    }
    return <></>;
  };

  const Footer = () => {
    if (showOrgBranding && publicOrgInfo?.brandLogoUrl && dateCreated) {
      return (
        <Flex alignItems="center" justifyContent="space-between">
          <Body color="neutral.600" textStyle="body200">
            {dateCreated}
          </Body>
          <Image
            alt={`${publicOrgInfo.displayName} logo`}
            filter="grayscale(1)"
            maxH="40px"
            maxW="120px"
            src={publicOrgInfo.brandLogoUrl}
          />
        </Flex>
      );
    }
    if (showOrgBranding && publicOrgInfo?.brandLogoUrl) {
      return (
        <Flex justifyContent="end">
          <Image
            alt={`${publicOrgInfo.displayName} logo`}
            filter="grayscale(1)"
            src={publicOrgInfo.brandLogoUrl}
            width="80px"
          />
        </Flex>
      );
    }
    if (dateCreated) {
      return (
        <Body color="neutral.600" textStyle="body200">
          {dateCreated}
        </Body>
      );
    }
    return <></>;
  };

  return (
    <Box cursor="pointer" flexDirection="column" onClick={handleProjectClick}>
      <SplitCard
        backgroundImage={imagePath}
        onResponsiveChangeOrientation={false}
      >
        <Body fontWeight="700" my="0" noOfLines={1} textOverflow="ellipsis">
          {title}
        </Body>
        <SubText />
        <Footer />
      </SplitCard>
    </Box>
  );
};

export { ProjectCard };
